import { useState } from 'react';
import { Pharmacy, PHARMACY_SERVICES } from '@/mocks/pharmacies';

interface PharmacyDetailProps {
  pharmacy: Pharmacy;
  onClose: () => void;
}

const PharmacyDetail = ({ pharmacy: p, onClose }: PharmacyDetailProps) => {
  const [activeTab, setActiveTab] = useState<'info' | 'schedule' | 'services'>('info');
  const accentColor = p.isOpen ? '#39ff14' : '#ff2d78';

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col"
      style={{
        background: 'rgba(10,10,20,0.98)',
        border: '1px solid rgba(255,45,120,0.3)',
        boxShadow: '0 0 40px rgba(255,45,120,0.08)',
        maxHeight: 'calc(100vh - 120px)',
      }}
    >
      {/* Top accent */}
      <div className="h-0.5 w-full shrink-0" style={{ background: 'linear-gradient(90deg, #ff2d78, #00f5ff, #39ff14, transparent)' }} />

      {/* Header */}
      <div className="px-5 py-4 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <div
                className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-mono text-[10px] font-bold"
                style={{ background: p.isOpen ? 'rgba(57,255,20,0.15)' : 'rgba(255,45,120,0.15)', border: `1px solid ${accentColor}40`, color: accentColor }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor, boxShadow: `0 0 4px ${accentColor}` }} />
                {p.is24h ? 'Работает 24/7' : p.isOpen ? 'Открыто сейчас' : 'Закрыто'}
              </div>
              {p.hasDriveThru && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px]"
                  style={{ background: 'rgba(240,255,0,0.1)', border: '1px solid rgba(240,255,0,0.25)', color: '#f0ff00' }}
                >
                  <i className="ri-car-line" />
                  Drive-thru
                </div>
              )}
            </div>
            <h2 className="font-orbitron font-black text-base text-white leading-tight">{p.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all shrink-0"
            style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.4)'; }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Rating row */}
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(s => (
              <i key={s} className={`text-xs ${s <= Math.round(p.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
            ))}
          </div>
          <span className="font-orbitron font-bold text-sm" style={{ color: '#f0ff00' }}>{p.rating}</span>
          <span className="font-mono text-xs text-white/30">({p.reviews} отзывов)</span>
          <span className="font-mono text-xs ml-auto" style={{ color: '#ff2d78' }}>
            <i className="ri-map-pin-line mr-1" />{p.distance}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-5 py-3 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {([
          { id: 'info', label: 'Информация', icon: 'ri-information-line' },
          { id: 'schedule', label: 'Расписание', icon: 'ri-calendar-line' },
          { id: 'services', label: 'Услуги', icon: 'ri-service-line' },
        ] as { id: typeof activeTab; label: string; icon: string }[]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
            style={activeTab === tab.id
              ? { background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78' }
              : { color: 'rgba(255,255,255,0.4)' }
            }
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

        {/* INFO TAB */}
        {activeTab === 'info' && (
          <>
            {/* Photo */}
            <div className="rounded-xl overflow-hidden h-40">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
            </div>

            {/* Info rows */}
            <div className="space-y-3">
              {[
                { icon: 'ri-map-pin-2-line', label: 'Адрес', value: p.address, color: '#ff2d78' },
                { icon: 'ri-time-line', label: 'Часы работы', value: p.hours, color: '#00f5ff' },
                { icon: 'ri-phone-line', label: 'Телефон', value: p.phone, color: '#39ff14' },
                { icon: 'ri-building-2-line', label: 'Район', value: p.district, color: '#f0ff00' },
              ].map(row => (
                <div key={row.label} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${row.color}12`, border: `1px solid ${row.color}25` }}>
                    <i className={`${row.icon} text-sm`} style={{ color: row.color }} />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-white/30 mb-0.5">{row.label}</p>
                    <p className="font-mono text-xs text-white/75">{row.value}</p>
                  </div>
                </div>
              ))}

              {/* Metro */}
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: p.metroColor + '20', border: `1px solid ${p.metroColor}40` }}>
                  <i className="ri-subway-line text-sm" style={{ color: p.metroColor }} />
                </div>
                <div>
                  <p className="font-mono text-[10px] text-white/30 mb-0.5">Метро</p>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: p.metroColor }} />
                    <p className="font-mono text-xs text-white/75">{p.metro}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5">
              {p.tags.map(tag => (
                <span key={tag} className="px-2.5 py-1 rounded-full font-mono text-[10px]"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="space-y-2.5 pt-1">
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(p.address)}`}
                target="_blank"
                rel="nofollow noreferrer"
                className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300"
                style={{ background: '#ff2d78', color: '#050508', boxShadow: '0 0 16px rgba(255,45,120,0.4)' }}
              >
                <i className="ri-navigation-line" />
                ПОСТРОИТЬ МАРШРУТ
              </a>
              <a
                href={`tel:${p.phone}`}
                className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-sm cursor-pointer transition-all duration-300"
                style={{ border: '1px solid rgba(57,255,20,0.35)', color: '#39ff14' }}
              >
                <i className="ri-phone-line" />
                {p.phone}
              </a>
            </div>
          </>
        )}

        {/* SCHEDULE TAB */}
        {activeTab === 'schedule' && (
          <div className="space-y-2">
            <p className="font-mono text-[10px] tracking-widest text-white/30 mb-4">РАСПИСАНИЕ РАБОТЫ</p>
            {p.schedule.map((day, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-4 py-3 rounded-xl transition-all"
                style={{
                  background: day.isToday ? 'rgba(255,45,120,0.08)' : 'rgba(255,255,255,0.02)',
                  border: day.isToday ? '1px solid rgba(255,45,120,0.3)' : '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <div className="flex items-center gap-3">
                  {day.isToday && (
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#ff2d78', boxShadow: '0 0 4px #ff2d78' }} />
                  )}
                  <span
                    className="font-mono text-xs w-6"
                    style={{ color: day.isToday ? '#ff2d78' : 'rgba(255,255,255,0.5)' }}
                  >
                    {day.day}
                  </span>
                  {day.isToday && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,45,120,0.15)', color: '#ff2d78' }}>
                      Сегодня
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {p.is24h ? (
                    <span className="font-orbitron font-bold text-xs" style={{ color: '#39ff14', textShadow: '0 0 8px rgba(57,255,20,0.5)' }}>
                      Круглосуточно
                    </span>
                  ) : (
                    <>
                      <span className="font-mono text-xs" style={{ color: day.isToday ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.55)' }}>
                        {day.open}
                      </span>
                      <span className="font-mono text-[10px] text-white/25">—</span>
                      <span className="font-mono text-xs" style={{ color: day.isToday ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.55)' }}>
                        {day.close}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SERVICES TAB */}
        {activeTab === 'services' && (
          <div className="space-y-2">
            <p className="font-mono text-[10px] tracking-widest text-white/30 mb-4">ДОСТУПНЫЕ УСЛУГИ</p>
            {PHARMACY_SERVICES.map(svc => {
              const available = p.services.includes(svc.id);
              return (
                <div
                  key={svc.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    background: available ? `${svc.color}08` : 'rgba(255,255,255,0.02)',
                    border: available ? `1px solid ${svc.color}25` : '1px solid rgba(255,255,255,0.05)',
                    opacity: available ? 1 : 0.45,
                  }}
                >
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0"
                    style={{ background: available ? `${svc.color}15` : 'rgba(255,255,255,0.05)', border: `1px solid ${available ? svc.color + '30' : 'rgba(255,255,255,0.08)'}` }}
                  >
                    <i className={`${svc.icon} text-base`} style={{ color: available ? svc.color : 'rgba(255,255,255,0.25)' }} />
                  </div>
                  <span className="font-mono text-sm flex-1" style={{ color: available ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)' }}>
                    {svc.label}
                  </span>
                  <div
                    className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                    style={{ background: available ? `${svc.color}20` : 'rgba(255,255,255,0.05)' }}
                  >
                    <i
                      className={available ? 'ri-check-line' : 'ri-close-line'}
                      style={{ color: available ? svc.color : 'rgba(255,255,255,0.2)', fontSize: '12px' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PharmacyDetail;
